import React, { useState, useEffect, useMemo } from "react";
import Context from "../../Context";
import { Header } from "../../components/modules";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import { useToast } from "../../components/ui";

// Icons
import {
  FaStar,
  FaPlus,
  FaTrashCan,
  FaPenToSquare,
  FaArrowUp,
  FaArrowDown,
  FaGift,
  FaCheck,
  FaXmark,
  FaMagnifyingGlass,
  FaUser,
  FaFloppyDisk,
  FaPowerOff,
  FaUserShield,
} from "react-icons/fa6";
import { FiFileText, FiAward, FiRefreshCw } from "react-icons/fi";

export const SurveysPage = () => {
  const { callApi, configs } = Context();
  const { toast } = useToast();

  // State ทั่วไป
  const [activeTab, setActiveTab] = useState("surveys"); // 'surveys' | 'reports'
  const [loading, setLoading] = useState(true);

  // State สำหรับสถานะเปิด/ปิดระบบแบบประเมิน ('closed' | 'admin' | 'open')
  const [surveyStatus, setSurveyStatus] = useState("closed");
  const [togglingStatus, setTogglingStatus] = useState(false);

  // State สำหรับของรางวัลรวมประจำแบบประเมิน (Reward Settings)
  const [rewardCouponId, setRewardCouponId] = useState("");
  const [cooldownDays, setCooldownDays] = useState(14);
  const [savingReward, setSavingReward] = useState(false);

  // State สำหรับชุดคำถาม (Manage Surveys)
  const [surveys, setSurveys] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [surveyFormData, setSurveyFormData] = useState({
    title: "",
    description: "",
    order: 1,
    questions: [],
  });

  // State สำหรับรายงานและผลการประเมิน (Reports)
  const [selectedRoundDate, setSelectedRoundDate] = useState(
    configs?.roundDate || ""
  );
  const [roundSummary, setRoundSummary] = useState(null);
  const [roundResponses, setRoundResponses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewingResponse, setViewingResponse] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  // คำนวณรายการที่รออนุมัติ
  const pendingResponses = useMemo(() => {
    return roundResponses.filter(
      (r) =>
        r.rewardStatus === "pending" ||
        (!r.rewardStatus && !r.couponOrderId && r.couponId)
    );
  }, [roundResponses]);

  // ดึงชุดคำถามและการตั้งค่า
  const fetchSurveysAndSettings = async () => {
    setLoading(true);
    try {
      const [surveysRes, settingsRes] = await Promise.all([
        callApi("surveys-getAllSurveys"),
        callApi("surveys-getSurveySettings"),
      ]);

      if (surveysRes?.success) {
        setSurveys(surveysRes.surveys || []);
      }
      if (settingsRes?.success && settingsRes.settings) {
        setRewardCouponId(settingsRes.settings.rewardCouponId || "");
        setCooldownDays(
          settingsRes.settings.cooldownDays !== undefined
            ? settingsRes.settings.cooldownDays
            : 14
        );
        setSurveyStatus(settingsRes.settings.status || "closed");
      }
    } catch (err) {
      console.error("fetchSurveysAndSettings error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoupons = async () => {
    try {
      const res = await callApi("coupons-getAllCoupon", { limit: 500 });
      if (Array.isArray(res)) {
        setCoupons(res);
      } else if (res?.coupons) {
        setCoupons(res.coupons);
      }
    } catch (err) {
      console.error("fetchCoupons error:", err);
    }
  };

  // ดึงรายงานประจำงวด
  const fetchRoundReports = async (roundDate) => {
    if (!roundDate) return;
    setLoading(true);
    try {
      const [sumRes, respRes] = await Promise.all([
        callApi("surveys-getRoundSummary", { roundDate }),
        callApi("surveys-getRoundResponses", { roundDate, limit: 200 }),
      ]);

      if (sumRes?.success) {
        setRoundSummary(sumRes.summary);
      }
      if (respRes?.success) {
        setRoundResponses(respRes.responses || []);
      }
    } catch (err) {
      console.error("fetchRoundReports error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSurveysAndSettings();
    fetchCoupons();
  }, []);

  useEffect(() => {
    if (activeTab === "reports" && selectedRoundDate) {
      fetchRoundReports(selectedRoundDate);
    }
  }, [activeTab, selectedRoundDate]);

  // บันทึกของรางวัลคูปอง
  // บันทึกของรางวัลคูปอง
  const handleSaveRewardSetting = async () => {
    setSavingReward(true);
    try {
      const res = await callApi("surveys-updateSurveySettings", {
        rewardCouponId,
        cooldownDays: Number(cooldownDays) || 14,
      });
      if (res?.success) {
        toast({ title: "บันทึกการตั้งค่าของรางวัลและ Cooldown สำเร็จ" });
      } else {
        alert(res?.message || "บันทึกไม่สำเร็จ");
      }
    } catch (err) {
      console.error("save reward setting error:", err);
    } finally {
      setSavingReward(false);
    }
  };

  // เปลี่ยนสถานะระบบแบบประเมิน ('closed' | 'admin' | 'open')
  const handleUpdateStatus = async (newStatus) => {
    if (newStatus === surveyStatus) return;
    const statusTitles = {
      closed: "ปิดระบบ (Closed)",
      admin: "เปิดเฉพาะแอดมิน (Admin Only)",
      open: "เปิดใช้งานทั่วไป (Open / Active)",
    };

    if (
      !window.confirm(
        `คุณต้องการเปลี่ยนสถานะแบบประเมินเป็น "${statusTitles[newStatus]}" ใช่หรือไม่?`
      )
    ) {
      return;
    }

    setTogglingStatus(true);
    try {
      const res = await callApi("surveys-updateSurveySettings", {
        status: newStatus,
      });
      if (res?.success) {
        setSurveyStatus(newStatus);
        toast({
          title: `เปลี่ยนสถานะเป็น "${statusTitles[newStatus]}" สำเร็จ`,
        });
      } else {
        alert(res?.message || "เปลี่ยนสถานะไม่สำเร็จ");
      }
    } catch (err) {
      console.error("update system status error:", err);
      alert("เกิดข้อผิดพลาดในการเปลี่ยนสถานะ");
    } finally {
      setTogglingStatus(false);
    }
  };

  // อนุมัติแจกคูปองรายคน
  const handleApproveReward = async (resItem) => {
    if (
      !window.confirm(
        `ต้องการอนุมัติแจกคูปองให้กับคุณ ${
          resItem.userData?.displayName ||
          resItem.userData?.firstName ||
          resItem.userId
        } ใช่หรือไม่?`
      )
    ) {
      return;
    }

    setActionLoadingId(resItem.id);
    try {
      const res = await callApi("surveys-approveReward", {
        responseId: resItem.id,
      });
      if (res?.success) {
        toast({ title: "อนุมัติแจกคูปองสำเร็จเรียบร้อย" });
        await fetchRoundReports(selectedRoundDate);
        if (viewingResponse && viewingResponse.id === resItem.id) {
          setViewingResponse((prev) => ({
            ...prev,
            rewardStatus: "approved",
            couponOrderId: res.couponOrderId || prev.couponOrderId,
          }));
        }
      } else {
        alert(res?.message || "อนุมัติไม่สำเร็จ");
      }
    } catch (err) {
      console.error("approve reward error:", err);
      alert("เกิดข้อผิดพลาดในการอนุมัติ");
    } finally {
      setActionLoadingId(null);
    }
  };

  // ปฏิเสธการแจกคูปองรายคน
  const handleRejectReward = async (resItem) => {
    if (
      !window.confirm(
        `ต้องการปฏิเสธการแจกคูปองให้กับคุณ ${
          resItem.userData?.displayName ||
          resItem.userData?.firstName ||
          resItem.userId
        } ใช่หรือไม่?`
      )
    ) {
      return;
    }

    setActionLoadingId(resItem.id);
    try {
      const res = await callApi("surveys-rejectReward", {
        responseId: resItem.id,
      });
      if (res?.success) {
        toast({ title: "ปฏิเสธการแจกรางวัลเรียบร้อย" });
        await fetchRoundReports(selectedRoundDate);
        if (viewingResponse && viewingResponse.id === resItem.id) {
          setViewingResponse((prev) => ({
            ...prev,
            rewardStatus: "rejected",
          }));
        }
      } else {
        alert(res?.message || "ปฏิเสธไม่สำเร็จ");
      }
    } catch (err) {
      console.error("reject reward error:", err);
      alert("เกิดข้อผิดพลาดในการปฏิเสธ");
    } finally {
      setActionLoadingId(null);
    }
  };

  // อนุมัติแจกคูปองทั้งหมดที่รอดำเนินการ (Bulk Approve)
  const handleBulkApproveReward = async () => {
    if (pendingResponses.length === 0) return;
    if (
      !window.confirm(
        `ต้องการอนุมัติแจกคูปองให้กับลูกค้าที่รอดำเนินการทั้งหมด ${pendingResponses.length} คนใช่หรือไม่?`
      )
    ) {
      return;
    }

    setActionLoadingId("bulk");
    try {
      const ids = pendingResponses.map((r) => r.id);
      const res = await callApi("surveys-approveReward", {
        responseIds: ids,
      });
      if (res?.success) {
        toast({
          title: `อนุมัติแจกคูปองสำเร็จ ${res.approvedCount || ids.length} รายการ`,
        });
        await fetchRoundReports(selectedRoundDate);
      } else {
        alert(res?.message || "อนุมัติไม่สำเร็จ");
      }
    } catch (err) {
      console.error("bulk approve error:", err);
      alert("เกิดข้อผิดพลาดในการอนุมัติทั้งหมด");
    } finally {
      setActionLoadingId(null);
    }
  };

  // Modal Handlers สำหรับชุดคำถาม
  const handleOpenCreateModal = () => {
    const nextOrder =
      surveys.length > 0
        ? Math.max(...surveys.map((s) => Number(s.order) || 1)) + 1
        : 1;

    setEditingSurvey(null);
    setSurveyFormData({
      title: `ชุดคำถามที่ ${nextOrder}`,
      description: "กรุณาตอบแบบประเมินความพึงพอใจในหัวข้อนี้",
      order: nextOrder,
      questions: [
        {
          id: `q_${Date.now()}_1`,
          type: "rating",
          title: "ความพึงพอใจต่อการใช้งานและการบริการ",
          required: true,
          maxScore: 5,
        },
        {
          id: `q_${Date.now()}_2`,
          type: "text",
          title: "ข้อเสนอแนะเพิ่มเติม",
          required: false,
        },
      ],
    });
    setIsSurveyModalOpen(true);
  };

  const handleOpenEditModal = (item) => {
    setEditingSurvey(item);
    setSurveyFormData({
      title: item.title || "",
      description: item.description || "",
      order: item.order || 1,
      questions: item.questions || [],
    });
    setIsSurveyModalOpen(true);
  };

  const handleSaveSurvey = async () => {
    if (!surveyFormData.title.trim()) {
      alert("กรุณากรอกชื่อชุดคำถาม");
      return;
    }

    setLoading(true);
    try {
      if (editingSurvey) {
        // Edit
        const res = await callApi("surveys-updateSurvey", {
          id: editingSurvey.id,
          ...surveyFormData,
        });
        if (res?.success) {
          toast({ title: "บันทึกการแก้ไขชุดคำถามสำเร็จ" });
          setIsSurveyModalOpen(false);
          fetchSurveysAndSettings();
        } else {
          alert(res?.message || "บันทึกไม่สำเร็จ");
        }
      } else {
        // Create
        const res = await callApi("surveys-createSurvey", surveyFormData);
        if (res?.success) {
          toast({ title: "สร้างชุดคำถามใหม่สำเร็จ" });
          setIsSurveyModalOpen(false);
          fetchSurveysAndSettings();
        } else {
          alert(res?.message || "สร้างไม่สำเร็จ");
        }
      }
    } catch (err) {
      console.error("save survey error:", err);
      alert("เกิดข้อผิดพลาดในการบันทึกข้อมูล");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSurvey = async (id, title) => {
    if (window.confirm(`คุณแน่ใจหรือไม่ว่าต้องการลบชุดคำถาม "${title}"?`)) {
      setLoading(true);
      try {
        const res = await callApi("surveys-deleteSurvey", { id });
        if (res?.success) {
          toast({ title: "ลบชุดคำถามเรียบร้อยแล้ว" });
          fetchSurveysAndSettings();
        } else {
          alert(res?.message || "ลบไม่สำเร็จ");
        }
      } catch (err) {
        console.error("delete survey error:", err);
      } finally {
        setLoading(false);
      }
    }
  };

  // จัดการลำดับการแสดงผล (Step Order)
  const handleMoveOrder = async (index, direction) => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= surveys.length) return;

    const currentItem = surveys[index];
    const targetItem = surveys[targetIndex];

    const currentOrder = currentItem.order || 1;
    const targetOrder = targetItem.order || 1;

    setLoading(true);
    try {
      await Promise.all([
        callApi("surveys-updateSurvey", {
          id: currentItem.id,
          order: targetOrder,
        }),
        callApi("surveys-updateSurvey", {
          id: targetItem.id,
          order: currentOrder,
        }),
      ]);
      toast({ title: "สลับลำดับหน้าชุดคำถามเรียบร้อย" });
      fetchSurveysAndSettings();
    } catch (err) {
      console.error("move order error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Question Form Helpers
  const handleAddQuestion = (type = "rating") => {
    const newQ = {
      id: `q_${Date.now()}`,
      type,
      title: type === "rating" ? "คำถามให้คะแนนความพึงพอใจ" : "ความคิดเห็นและข้อเสนอแนะ",
      required: type === "rating",
      maxScore: 5,
    };
    setSurveyFormData((prev) => ({
      ...prev,
      questions: [...prev.questions, newQ],
    }));
  };

  const handleRemoveQuestion = (idx) => {
    setSurveyFormData((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== idx),
    }));
  };

  const handleUpdateQuestion = (idx, field, value) => {
    setSurveyFormData((prev) => {
      const updated = [...prev.questions];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, questions: updated };
    });
  };

  // กรองรายการผู้ตอบตาม Search
  const filteredResponses = useMemo(() => {
    if (!searchTerm.trim()) return roundResponses;
    const s = searchTerm.toLowerCase();
    return roundResponses.filter((r) => {
      const phone = r.userData?.phone || "";
      const name = `${r.userData?.firstName || ""} ${r.userData?.lastName || ""} ${r.userData?.displayName || ""}`.toLowerCase();
      const userNo = String(r.userNo || "");
      return phone.includes(s) || name.includes(s) || userNo.includes(s);
    });
  }, [roundResponses, searchTerm]);

  return (
    <div className="h-fit w-full max-w-[1600px] mb-24 px-4">
      {/* Header */}
      <Header
        header="Satisfaction Survey"
        subHeader="ระบบแบบประเมินความพึงพอใจและของรางวัล"
        icon={<FaStar className="text-amber-500" />}
      />

      {/* Tabs */}
      <div className="flex flex-wrap items-center justify-between border-b border-gray-200 mt-6 gap-2">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setActiveTab("surveys")}
            className={`pb-3 px-4 font-bold text-sm transition-all flex items-center gap-2 border-b-2 ${
              activeTab === "surveys"
                ? "border-navy text-navy"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <FiAward className="w-4 h-4" />
            <span>จัดการชุดคำถาม & ของรางวัล ({surveys.length} หน้า)</span>
          </button>
          <button
            onClick={() => setActiveTab("reports")}
            className={`pb-3 px-4 font-bold text-sm transition-all flex items-center gap-2 border-b-2 ${
              activeTab === "reports"
                ? "border-navy text-navy"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <FiFileText className="w-4 h-4" />
            <span>รายงานและผลการประเมิน</span>
          </button>
        </div>

        <button
          type="button"
          onClick={() => {
            if (activeTab === "surveys") {
              fetchSurveysAndSettings();
              fetchCoupons();
            } else {
              fetchRoundReports(selectedRoundDate);
            }
          }}
          disabled={loading}
          className="mb-2 py-1.5 px-3 text-xs font-semibold rounded-md border border-gray-200 text-gray-600 hover:text-navy hover:border-navy bg-white flex items-center gap-1.5 shadow-2xs transition-all disabled:opacity-50 cursor-pointer"
        >
          <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin text-navy" : ""}`} />
          <span>รีเฟรชข้อมูล</span>
        </button>
      </div>

      {/* TAB 1: MANAGE SURVEYS & REWARD */}
      {activeTab === "surveys" && (
        loading ? (
          <div className="flex flex-col items-center justify-center py-24 bg-white rounded-md border border-gray-200 shadow-xs mt-6 gap-3">
            <div className="w-10 h-10 border-4 border-navy border-t-transparent rounded-full animate-spin" />
            <span className="text-sm font-semibold text-gray-500">
              กำลังโหลดข้อมูลแบบสอบถามและการตั้งค่า...
            </span>
          </div>
        ) : (
        <div className="mt-6 space-y-6">
          {/* ส่วนที่ 0: สถานะ เปิด/ปิด ระบบแบบประเมิน (3 สถานะ: closed / admin / open) */}
          <div
            className={`p-5 rounded-md border shadow-xs flex flex-col md:flex-row items-start md:items-center justify-between gap-4 transition-colors ${
              surveyStatus === "open"
                ? "bg-emerald-50/70 border-emerald-300"
                : surveyStatus === "admin"
                ? "bg-amber-50/70 border-amber-300"
                : "bg-rose-50/70 border-rose-300"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-lg shadow-xs shrink-0 ${
                  surveyStatus === "open"
                    ? "bg-emerald-100 text-emerald-600"
                    : surveyStatus === "admin"
                    ? "bg-amber-100 text-amber-600"
                    : "bg-rose-100 text-rose-600"
                }`}
              >
                {surveyStatus === "open" ? (
                  <FaCheck />
                ) : surveyStatus === "admin" ? (
                  <FaUserShield />
                ) : (
                  <FaPowerOff />
                )}
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-sm font-bold text-gray-900">
                    สถานะระบบแบบสอบถามความพึงพอใจ
                  </h3>
                  <span
                    className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full ${
                      surveyStatus === "open"
                        ? "bg-emerald-100 text-emerald-700 border border-emerald-300"
                        : surveyStatus === "admin"
                        ? "bg-amber-100 text-amber-700 border border-amber-300"
                        : "bg-rose-100 text-rose-700 border border-rose-300"
                    }`}
                  >
                    {surveyStatus === "open"
                      ? "● เปิดใช้งานทั่วไป (Open)"
                      : surveyStatus === "admin"
                      ? "● เปิดเฉพาะแอดมิน (Admin Only)"
                      : "○ ปิดระบบ (Closed)"}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  {surveyStatus === "open"
                    ? "ขณะนี้เปิดใช้งานทั่วไป ลูกค้าทุกคนที่เข้าตามเงื่อนไขจะเห็น Popup และสามารถทำแบบประเมินได้"
                    : surveyStatus === "admin"
                    ? "ขณะนี้เปิดเฉพาะแอดมิน ลูกค้าทั่วไปจะไม่เห็น Popup และทำแบบประเมินไม่ได้ (เฉพาะแอดมินที่เห็นเพื่อทดสอบ)"
                    : "ขณะนี้ปิดระบบแบบสอบถาม ทุกคนจะไม่เห็น Popup และไม่สามารถส่งคำตอบได้"}
                </p>
              </div>
            </div>

            {/* ปุ่มเลือก 3 สถานะ */}
            <div className="flex flex-wrap items-center gap-2 shrink-0 self-stretch sm:self-auto justify-end">
              <button
                type="button"
                onClick={() => handleUpdateStatus("closed")}
                disabled={togglingStatus || surveyStatus === "closed"}
                className={`py-2 px-3.5 text-xs font-bold rounded-md flex items-center gap-1.5 transition-all cursor-pointer ${
                  surveyStatus === "closed"
                    ? "bg-rose-600 text-white shadow-xs ring-2 ring-rose-400/50"
                    : "bg-white text-gray-700 border border-gray-200 hover:bg-rose-50 hover:text-rose-700"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <FaPowerOff className="w-3.5 h-3.5" />
                <span>ปิดระบบ</span>
              </button>

              <button
                type="button"
                onClick={() => handleUpdateStatus("admin")}
                disabled={togglingStatus || surveyStatus === "admin"}
                className={`py-2 px-3.5 text-xs font-bold rounded-md flex items-center gap-1.5 transition-all cursor-pointer ${
                  surveyStatus === "admin"
                    ? "bg-amber-600 text-white shadow-xs ring-2 ring-amber-400/50"
                    : "bg-white text-gray-700 border border-gray-200 hover:bg-amber-50 hover:text-amber-700"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <FaUserShield className="w-3.5 h-3.5" />
                <span>เฉพาะแอดมิน</span>
              </button>

              <button
                type="button"
                onClick={() => handleUpdateStatus("open")}
                disabled={togglingStatus || surveyStatus === "open"}
                className={`py-2 px-3.5 text-xs font-bold rounded-md flex items-center gap-1.5 transition-all cursor-pointer ${
                  surveyStatus === "open"
                    ? "bg-emerald-600 text-white shadow-xs ring-2 ring-emerald-400/50"
                    : "bg-white text-gray-700 border border-gray-200 hover:bg-emerald-50 hover:text-emerald-700"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <FaCheck className="w-3.5 h-3.5" />
                <span>เปิดทั่วไป</span>
              </button>
            </div>
          </div>

          {/* ส่วนที่ 1: ตั้งค่าคูปองของรางวัลหลัก */}
          <div className="bg-gradient-to-r from-amber-50/70 to-orange-50/50 p-5 rounded-md border border-amber-200 shadow-xs space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-lg">
                  <FaGift />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-amber-950">
                    ของรางวัลคูปองเมื่อทำแบบประเมินครบทุกชุด
                  </h3>
                  <p className="text-xs text-amber-800">
                    เมื่อลูกค้าตอบแบบประเมินครบทุกชุดจนถึงหน้ายืนยัน ระบบจะแจกคูปองนี้เข้ากระเป๋าของลูกค้าทันที (1 คน / 1 สิทธิ์ ต่องวด)
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleSaveRewardSetting}
                disabled={savingReward}
                className="py-2 px-4 bg-navy text-white text-xs font-bold rounded-md hover:bg-opacity-90 flex items-center gap-1.5 shadow-xs disabled:opacity-50"
              >
                <FaFloppyDisk className="w-3.5 h-3.5" />
                <span>{savingReward ? "กำลังบันทึก..." : "บันทึกของรางวัล"}</span>
              </button>
            </div>

            <div className="pt-1 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">
                  เลือกคูปองที่จะแจก:
                </label>
                <select
                  value={rewardCouponId}
                  onChange={(e) => setRewardCouponId(e.target.value)}
                  className="w-full p-2.5 border border-amber-300 rounded-md bg-white font-semibold text-xs text-gray-800 focus:outline-none focus:border-navy"
                >
                  <option value="">-- ไม่แจกคูปอง --</option>
                  {coupons.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} (ส่วนลด {c.discount || 0} ฿)
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">
                  ระยะเวลา Cooldown ในการทำซ้ำ (วัน):
                </label>
                <input
                  type="number"
                  min="1"
                  max="90"
                  value={cooldownDays}
                  onChange={(e) => setCooldownDays(e.target.value)}
                  placeholder="14"
                  className="w-full p-2.5 border border-amber-300 rounded-md bg-white font-semibold text-xs text-gray-800 focus:outline-none focus:border-navy"
                />
              </div>
            </div>
          </div>

          {/* ส่วนที่ 2: รายการชุดคำถาม (หน้า 1, หน้า 2, ...) */}
          <div className="space-y-4">
            <div className="flex justify-between items-center bg-white p-4 rounded-md border border-gray-200 shadow-xs">
              <div>
                <h3 className="text-sm font-bold text-gray-800">
                  ขั้นตอนและชุดคำถาม (หน้าละ 1 ชุด)
                </h3>
                <p className="text-xs text-gray-500">
                  ระบบจะแสดงคำถามทีละหน้าตามลำดับด้านล่าง เมื่อลูกค้าทำครบทุกหน้าจึงจะเข้าสู่หน้ากดยืนยันรับคูปอง
                </p>
              </div>
              <button
                onClick={handleOpenCreateModal}
                className="py-2.5 px-4 bg-navy text-white text-xs font-bold rounded-md hover:bg-opacity-90 flex items-center gap-2 shadow-xs"
              >
                <FaPlus className="w-3.5 h-3.5" />
                <span>เพิ่มชุดคำถามใหม่</span>
              </button>
            </div>

            {/* Table of Surveys */}
            <div className="bg-white rounded-md border border-gray-200 shadow-xs overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50/80">
                    <TableHead className="w-28 text-center">ลำดับหน้า (Step)</TableHead>
                    <TableHead>ชื่อชุดคำถาม</TableHead>
                    <TableHead className="w-36 text-center">จำนวนคำถามย่อย</TableHead>
                    <TableHead className="w-44 text-center">สลับลำดับหน้า</TableHead>
                    <TableHead className="w-28 text-center">จัดการ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {surveys.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-12 text-gray-400">
                        ยังไม่มีชุดคำถามในระบบ คลิก "เพิ่มชุดคำถามใหม่" เพื่อเริ่มต้น
                      </TableCell>
                    </TableRow>
                  ) : (
                    surveys.map((item, index) => {
                      const isFirst = index === 0;
                      const isLast = index === surveys.length - 1;

                      return (
                        <TableRow key={item.id} className="hover:bg-gray-50/60">
                          {/* Step Badge */}
                          <TableCell className="text-center">
                            <span className="inline-flex items-center justify-center px-2.5 py-1 text-xs font-bold rounded-full bg-blue-50 text-navy border border-blue-200">
                              หน้า {index + 1} (Step {item.order})
                            </span>
                          </TableCell>

                          {/* Title & Desc */}
                          <TableCell>
                            <div className="space-y-0.5">
                              <span className="font-bold text-gray-800 text-sm block">
                                {item.title}
                              </span>
                              {item.description && (
                                <span className="text-xs text-gray-500 line-clamp-1">
                                  {item.description}
                                </span>
                              )}
                            </div>
                          </TableCell>

                          {/* Questions Count */}
                          <TableCell className="text-center font-semibold text-sm text-gray-700">
                            {item.questions?.length || 0} ข้อ
                          </TableCell>

                          {/* Order Buttons */}
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                disabled={isFirst}
                                onClick={() => handleMoveOrder(index, "up")}
                                title="แสดงก่อนหน้านี้"
                                className="p-1.5 rounded border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                              >
                                <FaArrowUp className="w-3 h-3" />
                              </button>
                              <button
                                disabled={isLast}
                                onClick={() => handleMoveOrder(index, "down")}
                                title="แสดงหลังจากนี้"
                                className="p-1.5 rounded border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                              >
                                <FaArrowDown className="w-3 h-3" />
                              </button>
                            </div>
                          </TableCell>

                          {/* Actions */}
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleOpenEditModal(item)}
                                title="แก้ไขชุดคำถาม"
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                              >
                                <FaPenToSquare className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteSurvey(item.id, item.title)}
                                title="ลบชุดคำถาม"
                                className="p-1.5 text-rose-500 hover:bg-rose-50 rounded"
                              >
                                <FaTrashCan className="w-4 h-4" />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
        )
      )}

      {/* TAB 2: REPORTS & RESPONSES */}
      {activeTab === "reports" && (
        <div className="mt-6 space-y-5">
          {/* Round Date Selector */}
          <div className="flex flex-wrap items-center justify-between gap-4 bg-white p-4 rounded-md border border-gray-200 shadow-xs">
            <div className="flex items-center gap-3">
              <label className="text-xs font-bold text-gray-600">
                ใส่งวดที่ต้องการดู:
              </label>
              <select
                value={selectedRoundDate}
                onChange={(e) => setSelectedRoundDate(e.target.value)}
                className="text-sm font-semibold border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:border-navy"
              >
                {(configs?.roundDates || [configs?.roundDate]).map((rd) => (
                  <option key={rd} value={rd}>
                    งวดวันที่ {rd}
                  </option>
                ))}
              </select>
            </div>

            {/* Search Input */}
            <div className="relative w-72">
              <input
                type="text"
                placeholder="ค้นหาชื่อ, เบอร์โทร, userNo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full text-xs pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-navy"
              />
              <FaMagnifyingGlass className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2.5" />
            </div>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-md border border-gray-200 shadow-xs gap-3">
              <div className="w-10 h-10 border-4 border-navy border-t-transparent rounded-full animate-spin" />
              <span className="text-sm font-semibold text-gray-500">
                กำลังโหลดข้อมูลรายงานและผลการประเมิน...
              </span>
            </div>
          ) : (
            <>
              {/* Summary Dashboard Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-5 rounded-md border border-gray-200 shadow-xs flex items-center justify-between">
              <div>
                <span className="text-xs font-semibold text-gray-500 block">
                  ผู้ร่วมประเมินทั้งหมด
                </span>
                <span className="text-2xl font-bold text-navy mt-1 block">
                  {Number(roundSummary?.totalResponses || 0).toLocaleString()} คน
                </span>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-50 text-navy flex items-center justify-center text-xl">
                <FaUser />
              </div>
            </div>

            <div className="bg-white p-5 rounded-md border border-gray-200 shadow-xs flex items-center justify-between">
              <div>
                <span className="text-xs font-semibold text-gray-500 block">
                  คะแนนพึงพอใจเฉลี่ย
                </span>
                <div className="flex items-baseline gap-1 mt-1">
                  <span className="text-2xl font-bold text-amber-500">
                    {roundSummary?.overallAvgScore || 0}
                  </span>
                  <span className="text-xs text-gray-400 font-bold">/ 5.00 ⭐</span>
                </div>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-50 text-amber-500 flex items-center justify-center text-xl">
                <FaStar />
              </div>
            </div>

            <div className="bg-white p-5 rounded-md border border-amber-200 bg-amber-50/30 shadow-xs flex items-center justify-between">
              <div>
                <span className="text-xs font-semibold text-amber-800 block">
                  รอการอนุมัติคูปอง
                </span>
                <span className="text-2xl font-bold text-amber-600 mt-1 block">
                  {pendingResponses.length.toLocaleString()} คน
                </span>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-xl">
                <FaGift />
              </div>
            </div>

            <div className="bg-white p-5 rounded-md border border-emerald-200 bg-emerald-50/20 shadow-xs flex items-center justify-between">
              <div>
                <span className="text-xs font-semibold text-emerald-800 block">
                  อนุมัติแจกแล้ว
                </span>
                <span className="text-2xl font-bold text-emerald-600 mt-1 block">
                  {roundResponses
                    .filter((r) => r.rewardStatus === "approved" || r.couponOrderId)
                    .length.toLocaleString()}{" "}
                  คน
                </span>
              </div>
              <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xl">
                <FaCheck />
              </div>
            </div>
          </div>

          {/* Question Stats Breakdown */}
          {roundSummary?.questionStats &&
            Object.keys(roundSummary.questionStats).length > 0 && (
              <div className="bg-white p-5 rounded-md border border-gray-200 shadow-xs space-y-4">
                <h4 className="text-sm font-bold text-gray-800">
                  สรุปคะแนนเฉลี่ยแยกตามข้อคำถาม
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(roundSummary.questionStats).map(
                    ([qId, qStat], idx) => {
                      const scorePercent = (qStat.avgScore / 5) * 100;
                      return (
                        <div
                          key={qId}
                          className="p-3.5 bg-gray-50 rounded-md border border-gray-100 space-y-2"
                        >
                          <div className="flex justify-between items-center text-xs">
                            <span className="font-bold text-gray-700">
                              {qStat.title || `ข้อที่ ${idx + 1}`}
                            </span>
                            <span className="font-bold text-amber-600">
                              {qStat.avgScore} / 5.00 ⭐ ({qStat.count} คนตอบ)
                            </span>
                          </div>
                          {/* Progress Bar */}
                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-amber-400 rounded-full transition-all"
                              style={{ width: `${scorePercent}%` }}
                            />
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            )}

          {/* Table of Responses */}
          <div className="bg-white rounded-md border border-gray-200 shadow-xs overflow-hidden">
            <div className="px-5 py-3.5 border-b border-gray-100 flex flex-wrap justify-between items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-gray-800">
                  รายการการประเมินรายคน ({filteredResponses.length} รายการ)
                </span>
                {pendingResponses.length > 0 && (
                  <span className="bg-amber-100 text-amber-800 text-[11px] font-bold px-2 py-0.5 rounded-full border border-amber-200">
                    รออนุมัติ {pendingResponses.length} คน
                  </span>
                )}
              </div>

              {pendingResponses.length > 0 && (
                <button
                  type="button"
                  onClick={handleBulkApproveReward}
                  disabled={actionLoadingId === "bulk"}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-xs font-bold flex items-center gap-1.5 shadow-xs transition-colors disabled:opacity-50"
                >
                  <FaCheck className="w-3 h-3" />
                  <span>
                    {actionLoadingId === "bulk"
                      ? "กำลังอนุมัติ..."
                      : `อนุมัติทั้งหมดที่รอดำเนินการ (${pendingResponses.length} คน)`}
                  </span>
                </button>
              )}
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50/80">
                  <TableHead className="w-12 text-center">#</TableHead>
                  <TableHead className="w-40">วัน-เวลา</TableHead>
                  <TableHead>ผู้ใช้งาน</TableHead>
                  <TableHead className="w-28 text-center">คะแนนเฉลี่ย</TableHead>
                  <TableHead className="w-48">คูปองรางวัล</TableHead>
                  <TableHead className="w-32 text-center">สถานะรางวัล</TableHead>
                  <TableHead className="w-44 text-center">การกระทำ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredResponses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-gray-400">
                      ยังไม่มีรายการประเมินในงวดนี้
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredResponses.map((resItem, idx) => {
                    const ratingAnswers = (resItem.answers || []).filter(
                      (a) => a.score !== null && a.score !== undefined
                    );
                    const avg =
                      ratingAnswers.length > 0
                        ? (
                            ratingAnswers.reduce(
                              (acc, curr) => acc + Number(curr.score),
                              0
                            ) / ratingAnswers.length
                          ).toFixed(1)
                        : "-";

                    const dateStr = resItem.createdAt
                      ? new Date(resItem.createdAt).toLocaleString("th-TH")
                      : "-";

                    const isPending =
                      resItem.rewardStatus === "pending" ||
                      (!resItem.rewardStatus && !resItem.couponOrderId && resItem.couponId);
                    const isApproved =
                      resItem.rewardStatus === "approved" || !!resItem.couponOrderId;
                    const isRejected = resItem.rewardStatus === "rejected";

                    const isLoadingRow = actionLoadingId === resItem.id;

                    return (
                      <TableRow key={resItem.id} className="hover:bg-gray-50/60">
                        <TableCell className="text-center text-xs text-gray-500 font-semibold">
                          {idx + 1}
                        </TableCell>
                        <TableCell className="text-xs text-gray-600">
                          {dateStr}
                        </TableCell>
                        <TableCell>
                          <div className="text-xs">
                            <span className="font-bold text-gray-800 block">
                              {resItem.userData?.displayName ||
                                `${resItem.userData?.firstName || ""} ${
                                  resItem.userData?.lastName || ""
                                }` ||
                                "ลูกค้า"}
                            </span>
                            <span className="text-gray-500 block">
                              {resItem.userData?.phone || "-"} (ID: {resItem.userId})
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="inline-flex items-center gap-1 font-bold text-amber-500 text-xs bg-amber-50 px-2 py-1 rounded">
                            <FaStar className="w-3 h-3" />
                            <span>{avg}</span>
                          </span>
                        </TableCell>
                        <TableCell className="text-xs">
                          {resItem.couponName ? (
                            <span className="text-gray-700 font-semibold">
                              🎁 {resItem.couponName}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {isApproved ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <FaCheck className="w-2.5 h-2.5" />
                              <span>อนุมัติแล้ว</span>
                            </span>
                          ) : isRejected ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
                              <FaXmark className="w-2.5 h-2.5" />
                              <span>ปฏิเสธแล้ว</span>
                            </span>
                          ) : isPending ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                              <span>รออนุมัติ</span>
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            {isPending && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleApproveReward(resItem)}
                                  disabled={isLoadingRow}
                                  title="อนุมัติแจกคูปอง"
                                  className="p-1 px-2 text-[11px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded flex items-center gap-1 transition-colors disabled:opacity-50"
                                >
                                  <FaCheck className="w-2.5 h-2.5" />
                                  <span>อนุมัติ</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRejectReward(resItem)}
                                  disabled={isLoadingRow}
                                  title="ปฏิเสธ"
                                  className="p-1 px-2 text-[11px] font-bold text-rose-600 hover:bg-rose-50 border border-rose-200 rounded flex items-center gap-1 transition-colors disabled:opacity-50"
                                >
                                  <FaXmark className="w-2.5 h-2.5" />
                                  <span>ปฏิเสธ</span>
                                </button>
                              </>
                            )}
                            <button
                              type="button"
                              onClick={() => setViewingResponse(resItem)}
                              className="text-xs font-bold text-navy hover:underline px-1.5 py-1"
                            >
                              ดูคำตอบ
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          </>
          )}
        </div>
      )}

      {/* MODAL: CREATE / EDIT SURVEY */}
      {isSurveyModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
          <div className="relative w-full max-w-[700px] bg-white rounded-md shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
              <h3 className="text-base font-bold text-gray-800">
                {editingSurvey ? "แก้ไขชุดคำถาม" : "สร้างชุดคำถามใหม่"}
              </h3>
              <button
                onClick={() => setIsSurveyModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaXmark className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="sm:col-span-3 space-y-1">
                  <label className="font-bold text-gray-700">
                    ชื่อชุดคำถาม (หัวข้อหน้านี้) <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={surveyFormData.title}
                    onChange={(e) =>
                      setSurveyFormData({
                        ...surveyFormData,
                        title: e.target.value,
                      })
                    }
                    placeholder="เช่น ชุดที่ 1: ความพึงพอใจการใช้งานระบบ"
                    className="w-full p-2.5 border border-gray-200 rounded-md focus:outline-none focus:border-navy"
                  />
                </div>
                <div className="space-y-1">
                  <label className="font-bold text-gray-700">
                    ลำดับหน้า (Step)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={surveyFormData.order}
                    onChange={(e) =>
                      setSurveyFormData({
                        ...surveyFormData,
                        order: Number(e.target.value) || 1,
                      })
                    }
                    className="w-full p-2.5 border border-gray-200 rounded-md focus:outline-none focus:border-navy text-center font-bold"
                  />
                  <span className="text-[10px] text-gray-400 block">
                    (เลขน้อยแสดงก่อน)
                  </span>
                </div>
              </div>

              <div className="space-y-1">
                <label className="font-bold text-gray-700">คำอธิบาย</label>
                <input
                  type="text"
                  value={surveyFormData.description}
                  onChange={(e) =>
                    setSurveyFormData({
                      ...surveyFormData,
                      description: e.target.value,
                    })
                  }
                  placeholder="คำอธิบายสั้นๆ เกี่ยวกับหัวข้อนี้"
                  className="w-full p-2.5 border border-gray-200 rounded-md focus:outline-none focus:border-navy"
                />
              </div>

              {/* Questions Builder */}
              <div className="space-y-3 pt-2">
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                  <label className="font-bold text-sm text-gray-800">
                    รายการคำถามในชุดนี้ ({surveyFormData.questions.length} ข้อ)
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleAddQuestion("rating")}
                      className="px-2.5 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded font-bold hover:bg-amber-100 flex items-center gap-1"
                    >
                      <FaStar className="w-3 h-3" />
                      <span>เพิ่มข้อให้คะแนนดาว</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAddQuestion("text")}
                      className="px-2.5 py-1 bg-blue-50 text-navy border border-blue-200 rounded font-bold hover:bg-blue-100 flex items-center gap-1"
                    >
                      <FaPlus className="w-3 h-3" />
                      <span>เพิ่มข้อความเห็น</span>
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  {surveyFormData.questions.map((q, qIndex) => (
                    <div
                      key={q.id || qIndex}
                      className="p-3 bg-gray-50 border border-gray-200 rounded-md space-y-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-navy bg-white px-2 py-0.5 border border-gray-200 rounded text-[11px]">
                          ข้อที่ {qIndex + 1} ({q.type === "rating" ? "⭐ ให้ดาว 1-5" : "📝 กล่องความเห็น"})
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveQuestion(qIndex)}
                          className="text-rose-500 hover:text-rose-700 p-1"
                          title="ลบคำถามข้อนี้"
                        >
                          <FaTrashCan className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      <input
                        type="text"
                        value={q.title}
                        onChange={(e) =>
                          handleUpdateQuestion(qIndex, "title", e.target.value)
                        }
                        placeholder="พิมพ์ข้อความคำถาม..."
                        className="w-full p-2 border border-gray-300 rounded bg-white focus:outline-none focus:border-navy text-xs font-semibold"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsSurveyModalOpen(false)}
                className="py-2 px-4 text-xs font-semibold text-gray-500 hover:text-gray-700"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={handleSaveSurvey}
                disabled={loading}
                className="py-2 px-4 text-xs font-bold bg-navy text-white rounded-md hover:bg-opacity-90 disabled:opacity-50 flex items-center gap-1.5"
              >
                <FaCheck className="w-3 h-3" />
                <span>บันทึกข้อมูล</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: VIEW SINGLE RESPONSE DETAILS */}
      {viewingResponse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
          <div className="relative w-full max-w-[550px] bg-white rounded-md shadow-2xl flex flex-col max-h-[85vh] overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
              <div>
                <h3 className="text-sm font-bold text-gray-800">
                  รายละเอียดการตอบแบบประเมิน
                </h3>
                <span className="text-xs text-gray-500">
                  งวดวันที่ {viewingResponse.roundDate}
                </span>
              </div>
              <button
                onClick={() => setViewingResponse(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaXmark className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-4 text-xs">
              {/* User Info Box */}
              <div className="p-3.5 bg-blue-50/50 border border-blue-100 rounded-md">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="font-bold text-navy block text-sm">
                      {viewingResponse.userData?.displayName ||
                        `${viewingResponse.userData?.firstName || ""} ${
                          viewingResponse.userData?.lastName || ""
                        }` ||
                        "ลูกค้า"}
                    </span>
                    <div className="text-gray-600 mt-1 space-y-0.5">
                      <p>เบอร์โทร: {viewingResponse.userData?.phone || "-"}</p>
                      <p>
                        User ID: {viewingResponse.userId} (userNo:{" "}
                        {viewingResponse.userNo || "-"})
                      </p>
                      <p>
                        เวลาที่ตอบ:{" "}
                        {new Date(viewingResponse.createdAt).toLocaleString("th-TH")}
                      </p>
                    </div>
                  </div>

                  <div>
                    {viewingResponse.rewardStatus === "approved" ||
                    viewingResponse.couponOrderId ? (
                      <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <FaCheck className="w-3 h-3" />
                        <span>อนุมัติแล้ว</span>
                      </span>
                    ) : viewingResponse.rewardStatus === "rejected" ? (
                      <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
                        <FaXmark className="w-3 h-3" />
                        <span>ปฏิเสธแล้ว</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                        <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                        <span>รออนุมัติคูปอง</span>
                      </span>
                    )}
                  </div>
                </div>

                {viewingResponse.couponName && (
                  <div className="mt-2.5 pt-2 border-t border-blue-100/80 flex items-center justify-between text-xs">
                    <span className="text-gray-600">ของรางวัลที่จะได้รับ:</span>
                    <span className="font-bold text-emerald-700">
                      🎁 {viewingResponse.couponName}
                    </span>
                  </div>
                )}
              </div>

              {/* Answers List */}
              <div className="space-y-3">
                <h4 className="font-bold text-gray-800">คำตอบทั้งหมดจากทุกชุดคำถาม:</h4>
                {(viewingResponse.answers || []).map((ans, aIdx) => (
                  <div
                    key={ans.questionId || aIdx}
                    className="p-3 bg-gray-50 border border-gray-200 rounded-md space-y-1"
                  >
                    <span className="font-bold text-gray-700 block">
                      {ans.questionTitle || `คำถามข้อที่ ${aIdx + 1}`}
                    </span>
                    {ans.score !== null && ans.score !== undefined ? (
                      <div className="flex items-center gap-1 text-amber-500 font-bold text-sm">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <FaStar
                            key={s}
                            className={`w-4 h-4 ${
                              s <= ans.score ? "text-amber-400" : "text-gray-200"
                            }`}
                          />
                        ))}
                        <span className="ml-1 text-xs text-gray-600">
                          ({ans.score} / 5 คะแนน)
                        </span>
                      </div>
                    ) : null}

                    {ans.comment ? (
                      <div className="mt-1 p-2 bg-white border border-gray-200 rounded text-gray-800">
                        <span className="font-semibold text-gray-500 block text-[11px]">
                          ความคิดเห็น/ข้อเสนอแนะ:
                        </span>
                        <span>{ans.comment}</span>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>

            <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
              <div>
                {(viewingResponse.rewardStatus === "pending" ||
                  (!viewingResponse.rewardStatus &&
                    !viewingResponse.couponOrderId &&
                    viewingResponse.couponId)) && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleApproveReward(viewingResponse)}
                      disabled={actionLoadingId === viewingResponse.id}
                      className="py-1.5 px-3 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-md flex items-center gap-1.5 disabled:opacity-50 transition-colors shadow-xs"
                    >
                      <FaCheck className="w-3 h-3" />
                      <span>อนุมัติแจกคูปอง</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRejectReward(viewingResponse)}
                      disabled={actionLoadingId === viewingResponse.id}
                      className="py-1.5 px-3 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 text-xs font-bold rounded-md flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                    >
                      <FaXmark className="w-3 h-3" />
                      <span>ปฏิเสธ</span>
                    </button>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => setViewingResponse(null)}
                className="py-1.5 px-4 text-xs font-semibold bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveysPage;
